/**
 * Shared glossary data — extracted so the <GlossaryTerm> inline tooltip
 * component AND the /glossar page render from the same source of truth.
 *
 * Schema:
 *   { id, term, category, aliases?, answer, use? }
 *
 *   `aliases` enables the search filter AND lets inline-tooltip authors
 *   write `<GlossaryTerm word="NDVI">…</GlossaryTerm>` or
 *   `<GlossaryTerm word="vegetationsindex">…</GlossaryTerm>` — both
 *   resolve to the canonical NDVI entry.
 */

export const TERMS = [
  {
    id: "bestaeuber-tag", term: "Bestäuber-Tag", category: "Bienen",
    aliases: ["bienenflug", "bienenwetter", "tag", "bestaeuber"],
    answer: "Ein Tag, an dem Honig- und Wildbienen fliegen können — typischerweise ab ca. 12 °C, mit Sonne und ohne Dauerregen.",
    use: "Bodenblick markiert Tage mit „Bienen aktiv\" auf der Landing-Page, wenn die Tagestemperatur in DE/AT diesen Schwellwert überschreitet.",
  },
  {
    id: "bodenfeuchte", term: "Bodenfeuchte", category: "Wetter",
    aliases: ["soil moisture", "wasser", "boden", "vwc"],
    answer: "Wie viel Wasser im obersten Wurzelbereich (0–7 cm) verfügbar ist, gemessen als Volumen-Anteil. Werte: 0.05 = staubtrocken, 0.40 = wassergesättigt.",
    use: "Wir nutzen das ERA5-Modell-Feld direkt. Bei Werten <0.18 zeigt die Tages-Ampel ein Trocken-Warnsignal.",
  },
  {
    id: "copernicus", term: "Copernicus", category: "Satellit",
    aliases: ["esa", "satellit", "sentinel"],
    answer: "Erdbeobachtungs-Programm der ESA. Liefert kostenlose, offene Satelliten-Daten der Sentinel-Familie.",
    use: "Bodenblick zieht NDVI und Co. direkt aus Sentinel-2-Aufnahmen, alle 5 Tage über Mitteleuropa.",
  },
  {
    id: "dsgvo", term: "DSGVO", category: "Privacy",
    aliases: ["gdpr", "datenschutz", "privatsphaere"],
    answer: "EU-Datenschutz-Grundverordnung. Regelt, wie personenbezogene Daten verarbeitet werden dürfen.",
    use: "Bodenblick erhebt keine personenbezogenen Daten. Keine Login-Pflicht, keine Tracking-Cookies.",
  },
  {
    id: "era5", term: "ERA5", category: "Wetter",
    aliases: ["reanalyse", "ecmwf", "klima"],
    answer: "Globale Wetter-Reanalyse des ECMWF — kombiniert historische Messungen mit einem Klima-Modell zu einem lückenlosen Datensatz seit 1940.",
    use: "Bodenblick benutzt ERA5 für die „Damals vs. Heute\"-Vergleiche und aktuelle Bodenfeuchte-Werte.",
  },
  {
    id: "feldfrucht", term: "Feldfrucht", category: "Agrar",
    aliases: ["kultur", "anbau", "feldkultur"],
    answer: "Eine landwirtschaftlich angebaute Pflanze — z. B. Winterweizen, Mais, Raps, Zuckerrübe oder Grünland.",
    use: "Bodenblick erkennt die wahrscheinlichste Feldfrucht aus dem NDVI-Jahresverlauf.",
  },
  {
    id: "firms", term: "FIRMS (NASA)", category: "Brände",
    aliases: ["nasa", "waldbrand", "feuer", "hot spot"],
    answer: "Fire Information for Resource Management System der NASA. Hot-Spot-Detektionen aus VIIRS- und MODIS-Satelliten in nahezu Echtzeit.",
    use: "Bodenblick zählt FIRMS-Hot-Spots der letzten 24 Stunden für Welt, Europa und DE/AT.",
  },
  {
    id: "frp", term: "FRP", category: "Brände",
    aliases: ["fire radiative power", "feuerstaerke", "megawatt"],
    answer: "Fire Radiative Power, also Brand-Strahlungsleistung in Megawatt. Maß für die Intensität eines Hot-Spots.",
    use: "Maximaler FRP-Wert der letzten 24 h auf der Landing-Page. >100 MW = größeres Waldbrand-Ereignis.",
  },
  {
    id: "fruktan", term: "Fruktan", category: "Weide",
    aliases: ["zucker", "pferd", "hufrehe", "stress"],
    answer: "Speicher-Zucker im Grashalm. Bildet sich bei kalten Nächten + sonnigen Tagen — gefährlich für Pferde (Hufrehe).",
    use: "Die Tages-Ampel warnt bei <5 °C Nacht-Temperatur + mehr als 6 Sonnenstunden.",
  },
  {
    id: "gdd", term: "GDD", category: "Agrar",
    aliases: ["wärmesumme", "growing degree days", "temperatursumme"],
    answer: "Wärmesumme: kumulierte Temperatur über einer Schwelle (meist 6 °C) seit Jahresbeginn.",
    use: "Bodenblick erkennt am GDD-Verlauf, ob Mais (hoher Bedarf) oder Gerste (niedrig) angebaut wird.",
  },
  {
    id: "gndvi", term: "GNDVI", category: "Satellit",
    aliases: ["green ndvi"],
    answer: "Variante des NDVI, die grünes statt rotes Licht nutzt. Empfindlicher für Stickstoff und Chlorophyll.",
    use: "Bodenblick zieht GNDVI als ergänzendes Signal heran, um Düngungsbedarf bei Mais und Weizen einzuschätzen.",
  },
  {
    id: "hufrehe", term: "Hufrehe", category: "Weide",
    aliases: ["pferd", "krankheit", "fruktan"],
    answer: "Schmerzhafte Entzündung der Huflederhaut beim Pferd, oft durch fruktanreiches Gras ausgelöst.",
    use: "Bodenblick warnt Pferdehalter:innen über die Tages-Ampel und das Fructan-Widget.",
  },
  {
    id: "ndvi", term: "NDVI", category: "Satellit",
    aliases: ["normalized difference", "vegetationsindex", "gruen"],
    answer: "Normalized Difference Vegetation Index — die bekannteste Zahl für Pflanzen-Vitalität aus dem Weltall. Werte: 0.1 = nackter Boden, 0.5 = mittlere Vegetation, 0.85 = voll im Saft.",
    use: "Bodenblick berechnet NDVI alle 5 Tage aus Sentinel-2-Bändern.",
  },
  {
    id: "ndwi", term: "NDWI", category: "Satellit",
    aliases: ["wasser-index", "feuchtigkeit"],
    answer: "Normalized Difference Water Index. Misst den Wassergehalt der Pflanzen-Blätter aus dem Infrarot.",
    use: "Bodenblick kombiniert NDWI mit Bodenfeuchte, um Trocken-Stress früher zu erkennen.",
  },
  {
    id: "nel", term: "NEL", category: "Weide",
    aliases: ["netto energie laktation", "milch", "energie"],
    answer: "Netto-Energie Laktation in Megajoule pro kg Trockenmasse. Wie viel verwertbare Energie das Futter dem Milchvieh liefert.",
    use: "Auf der Weide-&-Futter-Seite: >6.5 MJ ist Top, <5.5 grenzwertig.",
  },
  {
    id: "open-meteo", term: "Open-Meteo", category: "Wetter",
    aliases: ["wetterdaten", "api", "icon"],
    answer: "Open-Source-Wetter-API. Macht ECMWF-, ICON- und ERA5-Modelle kostenlos zugänglich. Keine API-Keys.",
    use: "Bodenblick zieht alle Wetterdaten ausschließlich über Open-Meteo.",
  },
  {
    id: "pulse", term: "Pulse Dashboard", category: "Bodenblick",
    aliases: ["live", "dashboard", "landing"],
    answer: "Der Live-Bereich der Startseite mit aktuell 7 Kacheln: Stress-Hotspot, Waldbrand, Bienen, Frost/Hitze, Vital, Bodenfeuchte, Damals vs. Heute.",
    use: "Aktualisiert sich alle 5 Minuten.",
  },
  {
    id: "rohprotein", term: "Rohprotein", category: "Weide",
    aliases: ["xp", "eiweiss", "protein"],
    answer: "Eiweiß-Anteil im Futter in Prozent der Trockenmasse. Junges Gras: 18–25%. Alter Heuschnitt: <10%.",
    use: "Bodenblick schätzt Rohprotein aus NDVI und Wachstumstempo der letzten 14 Tage.",
  },
  {
    id: "sentinel", term: "Sentinel-2", category: "Satellit",
    aliases: ["esa", "satellit"],
    answer: "ESA-Satellitenpaar (S2A + S2B). Alle 5 Tage über Mitteleuropa, 10 m Auflösung, 13 Spektralbänder.",
    use: "Hauptquelle für NDVI, NDWI, GNDVI auf Bodenblick.",
  },
  {
    id: "schulgarten", term: "Schulgarten", category: "Bodenblick",
    aliases: ["schule", "kinder", "polygon"],
    answer: "Eine kleine Fläche (max. 1 Hektar), die eine Schule mit Schul-Code auf Bodenblick anlegen kann.",
    use: "Frei für Schulen, anonymisiert unter /schulgaerten sichtbar.",
  },
  {
    id: "tages-ampel", term: "Tages-Ampel", category: "Bodenblick",
    aliases: ["quick", "ampel", "score"],
    answer: "Bodenblick-Score von 0–100 pro Region, kombiniert mit einer Farbe (rot / gelb / grün).",
    use: "Erreichbar unter /quick. Beruht auf NDVI, Bodenfeuchte und Wetter-Anomalien.",
  },
];

export const CATEGORY_COLOR = {
  Satellit: "#00FA9A",
  Wetter: "#3D2A78",
  Brände: "#FF7A45",
  Bienen: "#FFC24B",
  Weide: "#D9A87C",
  Agrar: "#7EE8B0",
  Privacy: "#1A1A1A",
  Bodenblick: "#00FA9A",
};

/**
 * Look up a glossary entry by term name OR alias (case-insensitive,
 * stripped of trailing punctuation). Returns null if nothing matches.
 */
export function findTerm(query) {
  if (!query) return null;
  const q = String(query).trim().toLowerCase().replace(/[.,;:!?]+$/, "");
  for (const t of TERMS) {
    if (t.term.toLowerCase() === q) return t;
    if ((t.aliases || []).some((a) => a.toLowerCase() === q)) return t;
  }
  return null;
}
